import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";
import { services, sectors, company } from "@/data/content";

const inputClass =
  "w-full bg-ink border border-border text-white placeholder:text-slatetxt/60 px-4 py-3.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors";

export const EnquiryForm = ({ type = "contact" }) => {
  const isQuote = type === "quote";
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", sector: "", location: "", message: "",
  });
  const [selected, setSelected] = useState([]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleService = (title) =>
    setSelected((s) => (s.includes(title) ? s.filter((x) => x !== title) : [...s, title]));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, type, services: selected });
      setDone(true);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="enquiry-success" className="bg-surface border border-border p-10 text-center">
        <CheckCircle2 size={48} className="mx-auto text-brand-light" />
        <h3 className="mt-6 font-display font-bold text-2xl text-white">Enquiry received</h3>
        <p className="mt-3 text-slatetxt max-w-md mx-auto">
          One of our team will be in touch shortly. For urgent matters call{" "}
          <span className="text-white">{company.phone}</span>.
        </p>
      </div>
    );
  }

  return (
    <form data-testid={`${type}-form`} onSubmit={onSubmit} className="bg-surface border border-border p-6 md:p-10 space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-2">Full name *</label>
          <input data-testid="form-name" className={inputClass} value={form.name} onChange={update("name")} placeholder="Jane Smith" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-2">Email *</label>
          <input data-testid="form-email" type="email" className={inputClass} value={form.email} onChange={update("email")} placeholder="jane@company.co.uk" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-2">Phone</label>
          <input data-testid="form-phone" className={inputClass} value={form.phone} onChange={update("phone")} placeholder="020 0000 0000" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-2">Company</label>
          <input data-testid="form-company" className={inputClass} value={form.company} onChange={update("company")} placeholder="Your organisation" />
        </div>
      </div>

      {isQuote && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-2">Sector</label>
              <select data-testid="form-sector" className={inputClass} value={form.sector} onChange={update("sector")}>
                <option value="">Select a sector</option>
                {sectors.map((s) => (
                  <option key={s.title} value={s.title}>{s.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-2">Site location(s)</label>
              <input data-testid="form-location" className={inputClass} value={form.location} onChange={update("location")} placeholder="e.g. London, Manchester" />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-3">Services required</label>
            <div className="flex flex-wrap gap-2.5">
              {services.map((s) => {
                const active = selected.includes(s.title);
                return (
                  <button
                    type="button"
                    key={s.slug}
                    data-testid={`form-service-${s.slug}`}
                    onClick={() => toggleService(s.title)}
                    className={`text-sm px-4 py-2 border transition-colors duration-200 ${
                      active
                        ? "bg-brand border-brand text-white"
                        : "bg-ink border-border text-slatetxt hover:border-brand/60 hover:text-white"
                    }`}
                  >
                    {s.title}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div>
        <label className="block text-xs uppercase tracking-[0.15em] text-slatetxt mb-2">
          {isQuote ? "Tell us about your requirement" : "Message"}
        </label>
        <textarea data-testid="form-message" rows={5} className={inputClass} value={form.message} onChange={update("message")} placeholder="How can we help?" />
      </div>

      <button
        type="submit"
        data-testid="form-submit"
        disabled={submitting}
        className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-semibold px-8 py-4 transition-colors duration-300"
      >
        {submitting && <Loader2 size={18} className="animate-spin" />}
        {isQuote ? "Request a Quote" : "Send Enquiry"}
      </button>
    </form>
  );
};
